import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export function LoginPage() {
  const { login, authError } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (authError) {
      setStatus('error');
      setError(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === 'sending' || cooldown > 0) return;

    setStatus('sending');
    setError('');

    const result = await login(email.trim());
    if (result.ok) {
      setStatus('success');
      setCooldown(60);
    } else {
      setStatus('error');
      setError(result.error || 'Something went wrong.');
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !email.trim()) return;
    setStatus('sending');
    const result = await login(email.trim());
    if (result.ok) {
      setStatus('success');
      setCooldown(60);
    } else {
      setStatus('error');
      setError(result.error || 'Something went wrong.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #F4F5F9 0%, #EEF0F5 40%, #E8ECF4 100%)' }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #0D9488 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full opacity-[0.03]"
          style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)' }}
        />
      </div>

      <motion.div
        className="w-full max-w-sm relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <img src="/alterme-logo.png" alt="AlterMe" className="h-12 mx-auto mb-5" style={{ mixBlendMode: 'multiply' }} />
          <h1 className="text-display font-bold text-primary tracking-tight">Product Configurator</h1>
          <p className="text-sm text-secondary mt-1.5">Sign in with your email to continue</p>
        </motion.div>

        {status === 'success' ? (
          <motion.div
            className="card text-center py-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-metric-positive/10 border border-metric-positive/20 mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <CheckCircle size={28} className="text-metric-positive" />
            </motion.div>
            <h2 className="text-lg font-semibold text-primary mb-2">Check your inbox</h2>
            <p className="text-sm text-secondary mb-1">
              We sent a sign-in link to
            </p>
            <p className="text-sm font-semibold text-primary mb-6">{email}</p>
            <p className="text-xs text-muted mb-4">The link expires in 15 minutes.</p>
            <button
              onClick={handleResend}
              disabled={cooldown > 0}
              className="text-sm text-chart-teal hover:text-teal-600 font-semibold disabled:text-muted disabled:cursor-not-allowed transition-colors"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend link'}
            </button>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            className="card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            style={{
              ...(status === 'error' && error ? {} : {}),
            }}
          >
            <label className="block text-xs text-secondary uppercase tracking-wider mb-2 font-semibold">
              Email Address
            </label>
            <div className="relative mb-4">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
                <Mail size={16} />
              </div>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                className={`w-full input-field pl-10 text-base ${status === 'error' ? 'ring-2 ring-metric-negative/40 border-metric-negative/40' : ''}`}
                placeholder="you@alterme.com"
                autoFocus
                disabled={status === 'sending'}
              />
            </div>

            {status === 'error' && error && (
              <motion.div
                className="flex items-start gap-2 mb-3 p-3 rounded-xl bg-metric-negative/5 border border-metric-negative/15"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <AlertCircle size={14} className="text-metric-negative mt-0.5 shrink-0" />
                <p className="text-xs text-metric-negative">{error}</p>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={status === 'sending' || !email.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {status === 'sending' ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending link...
                </>
              ) : (
                <>
                  Send sign-in link
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-micro text-muted text-center mt-3.5">
              Only @alterme.com email addresses can access this tool.
            </p>
          </motion.form>
        )}
      </motion.div>
    </div>
  );
}
